import {
  apiFetch,
  boundedErrorText,
  responseReasonCode,
  responseSupport,
  type ResponseSupport,
} from "@/lib/api";

/**
 * Admin signer-device routes (SGN-110, ALL-5839 lane f15).
 *
 * Three live gateway routes, all browser-session only:
 *  - GET  /v1/admin/signer-rebinds?state=PENDING  — the approval queue of
 *    devices waiting to replace the signer on a wallet.
 *  - GET  /v1/admin/signer-devices?principal_id=  — one person's devices. The
 *    relay REQUIRES a principal_id; callers must never call it without one.
 *  - POST /v1/admin/signer-rebinds/{id}/approve   — an existing route that
 *    needs a fresh AAL2 step-up, so callers wrap it in withStepUpRetry exactly
 *    as pair-signer-device.tsx wraps createSignerPairing.
 *
 * Request/auth mechanics are copied from createSignerPairing in lib/api.ts;
 * the error envelope is shaped with the same exported helpers
 * (boundedErrorText / responseReasonCode / responseSupport) so the two error
 * shapes the gateway returns (AuthReasonCode camelCase and RFC 7807
 * snake_case) both map the same way. No scope, permission or route is changed
 * here — this file only reads and calls the routes the lane declares.
 */

/** The five states a rebind request can be in. A PENDING row past its
 * `expiresAt` is reported by the relay as EXPIRED. */
export type SignerRebindState = "PENDING" | "APPROVED" | "REJECTED" | "REDEEMED" | "EXPIRED";

export interface SignerRebind {
  id: string;
  walletId: string;
  fromDeviceId: string;
  toDeviceId: string;
  state: SignerRebindState | string;
  reason: string;
  requestedBy: string;
  requestedAt: string;
  expiresAt: string;
  decidedBy?: string;
  decisionReason?: string;
}

export interface SignerDevice {
  deviceId: string;
  walletId: string;
  state: string;
  boundAt: string;
  revokedAt?: string;
}

export interface SignerRebindListResult extends ResponseSupport {
  items: SignerRebind[];
  nextCursor?: string;
  error?: string;
  httpStatus?: number;
}

export interface SignerDeviceListResult extends ResponseSupport {
  items: SignerDevice[];
  nextCursor?: string;
  error?: string;
  httpStatus?: number;
}

export interface SignerRebindDecisionResult extends ResponseSupport {
  ok: boolean;
  /** The decided request as the gateway returns it (state APPROVED on success). */
  rebind?: SignerRebind;
  reasonCode?: string;
  error?: string;
  httpStatus?: number;
}

function str(value: unknown): string | undefined {
  return boundedErrorText(value);
}

/** Map one rebind row, dropping anything the contract does not promise rather
 * than inventing a placeholder (an id-less row renders as an honest dash). */
export function mapSignerRebind(raw: Record<string, unknown>): SignerRebind {
  return {
    id: str(raw.id) ?? "",
    walletId: str(raw.walletId) ?? "",
    fromDeviceId: str(raw.fromDeviceId) ?? "",
    toDeviceId: str(raw.toDeviceId) ?? "",
    state: str(raw.state) ?? "",
    reason: str(raw.reason) ?? "",
    requestedBy: str(raw.requestedBy) ?? "",
    requestedAt: str(raw.requestedAt) ?? "",
    expiresAt: str(raw.expiresAt) ?? "",
    decidedBy: str(raw.decidedBy),
    decisionReason: str(raw.decisionReason),
  };
}

export function mapSignerDevice(raw: Record<string, unknown>): SignerDevice {
  return {
    deviceId: str(raw.deviceId) ?? "",
    walletId: str(raw.walletId) ?? "",
    state: str(raw.state) ?? "",
    boundAt: str(raw.boundAt) ?? "",
    revokedAt: str(raw.revokedAt),
  };
}

export async function fetchSignerRebinds(
  options: { cursor?: string; limit?: number } = {},
): Promise<SignerRebindListResult> {
  const params = new URLSearchParams({ state: "PENDING" });
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.limit) params.set("limit", String(options.limit));
  try {
    const res = await apiFetch(`/v1/admin/signer-rebinds?${params.toString()}`, { method: "GET" }, "signer_rebinds_list");
    const data = await res.json().catch(() => ({}));
    const body = (data ?? {}) as Record<string, unknown>;
    if (!res.ok) {
      return {
        items: [],
        error: boundedErrorText(body.message) || responseReasonCode(body) || "Could not load the approval queue",
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    const rawItems = Array.isArray(body.items) ? body.items : [];
    return {
      items: rawItems.map((row) => mapSignerRebind((row ?? {}) as Record<string, unknown>)),
      nextCursor: str(body.nextCursor),
      ...responseSupport(res, data),
    };
  } catch {
    return { items: [], error: "Network error" };
  }
}

/** GET /v1/admin/signer-devices. The relay requires the principal_id query
 * parameter, so an empty id is refused here rather than sent as a request that
 * could return the whole org's devices. */
export async function fetchSignerDevices(principalId: string): Promise<SignerDeviceListResult> {
  const trimmed = principalId.trim();
  if (!trimmed) {
    return { items: [], error: "Enter a person's principal id to look up their devices" };
  }
  const params = new URLSearchParams({ principal_id: trimmed });
  try {
    const res = await apiFetch(`/v1/admin/signer-devices?${params.toString()}`, { method: "GET" }, "signer_devices_list");
    const data = await res.json().catch(() => ({}));
    const body = (data ?? {}) as Record<string, unknown>;
    if (!res.ok) {
      return {
        items: [],
        error: boundedErrorText(body.message) || responseReasonCode(body) || "Could not load this person's devices",
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    const rawItems = Array.isArray(body.items) ? body.items : [];
    return {
      items: rawItems.map((row) => mapSignerDevice((row ?? {}) as Record<string, unknown>)),
      nextCursor: str(body.nextCursor),
      ...responseSupport(res, data),
    };
  } catch {
    return { items: [], error: "Network error" };
  }
}

/**
 * POST /v1/admin/signer-rebinds/{id}/approve (existing route). Needs a fresh
 * AAL2 step-up — the caller wraps this in withStepUpRetry so the retry replays
 * the exact same request. Refusals arrive as problem bodies whose reasonCode
 * the page maps to a plain sentence (REBIND_SELF_APPROVAL, PRINCIPAL_NOT_HUMAN,
 * REBIND_WINDOW_EXPIRED, REBIND_ALREADY_DECIDED, BINDING_STATE_CONFLICT,
 * AUTH_INSUFFICIENT_SCOPE).
 */
export async function approveSignerRebind(id: string): Promise<SignerRebindDecisionResult> {
  try {
    const res = await apiFetch(
      `/v1/admin/signer-rebinds/${encodeURIComponent(id)}/approve`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
      "signer_rebind_approve",
    );
    const data = await res.json().catch(() => ({}));
    const body = (data ?? {}) as Record<string, unknown>;
    if (!res.ok) {
      return {
        ok: false,
        error: boundedErrorText(body.message) || responseReasonCode(body) || "Approval failed",
        reasonCode: responseReasonCode(body),
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    // The success body is the decided request itself; some relay builds wrap
    // it under `rebind`. Handle both without inventing a row.
    const decided = (body.rebind && typeof body.rebind === "object" ? body.rebind : body) as Record<string, unknown>;
    return {
      ok: true,
      rebind: mapSignerRebind(decided),
      ...responseSupport(res, data),
    };
  } catch {
    return { ok: false, error: "Network error" };
  }
}
